/**
 * Reminders on a mission: who to tell, and on what trigger.
 *
 *   {
 *     id,
 *     trigger: 'at_time' | 'when_available',
 *     at: iso | null,          // next fire, for at_time
 *     to: { type: 'me' },
 *     note,
 *     forStart,
 *     recur,                   // present when the at_time reminder repeats
 *     hold,                    // wait-for: ringing asks, it does not delete the reminder
 *     askedAt,                 // occurrence that already asked (wait-for)
 *     createdAt,
 *   }
 *
 * `recur` is a schedule from recurringSchedule.js. One-shot reminders omit it.
 */

import { normalizeRecur, scheduleRecurring } from './recurringSchedule';

export const REMINDER_TRIGGER = {
  AT_TIME: 'at_time',
  WHEN_AVAILABLE: 'when_available',
};

export const REMINDER_TO_ME = { type: 'me' };

/** Paste starts a recurring series over, so the clone does not inherit a used-up count. */
export function restartRecurringReminder(reminder, nowMs) {
  if (!reminder?.recur) return reminder;
  const when = Number.isFinite(nowMs) ? nowMs : Date.now();
  const scheduled = scheduleRecurring(reminder.recur, when);
  if (!scheduled) return reminder;
  return { ...reminder, at: scheduled.at, recur: scheduled.recur };
}

export function newReminderId() {
  return `rem_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeTo(to) {
  if (to && typeof to === 'object' && typeof to.type === 'string' && to.type) {
    return { type: to.type, id: to.id || undefined };
  }
  return { ...REMINDER_TO_ME };
}

function normalizeAt(value) {
  if (value == null || value === '') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const NOTE_MAX = 160;

export function normalizeReminderNote(value) {
  if (value == null) return '';
  const note = String(value).replace(/\s+/g, ' ').trim();
  return note.slice(0, NOTE_MAX);
}

export function normalizeReminder(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const trigger =
    raw.trigger === REMINDER_TRIGGER.WHEN_AVAILABLE
      ? REMINDER_TRIGGER.WHEN_AVAILABLE
      : raw.trigger === REMINDER_TRIGGER.AT_TIME
        ? REMINDER_TRIGGER.AT_TIME
        : null;
  if (!trigger) return null;
  const at = trigger === REMINDER_TRIGGER.AT_TIME ? normalizeAt(raw.at) : null;
  if (trigger === REMINDER_TRIGGER.AT_TIME && !at) return null;
  const note = normalizeReminderNote(raw.note);
  const recur = trigger === REMINDER_TRIGGER.AT_TIME && raw.forStart !== true
    ? normalizeRecur(raw.recur)
    : null;
  const hold = trigger === REMINDER_TRIGGER.AT_TIME && raw.hold === true;
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : newReminderId(),
    trigger,
    at,
    to: normalizeTo(raw.to),
    note: note || undefined,
    forStart: trigger === REMINDER_TRIGGER.AT_TIME && !recur && raw.forStart === true ? true : undefined,
    recur: recur || undefined,
    hold: hold || undefined,
    askedAt: hold ? (normalizeAt(raw.askedAt) || undefined) : undefined,
    createdAt: normalizeAt(raw.createdAt) || new Date().toISOString(),
  };
}

export function normalizeReminders(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    const reminder = normalizeReminder(raw);
    if (!reminder || seen.has(reminder.id)) continue;
    seen.add(reminder.id);
    out.push(reminder);
  }
  return out;
}

export function remindersEqual(a, b) {
  const left = normalizeReminders(a);
  const right = normalizeReminders(b);
  if (left.length !== right.length) return false;
  return left.every((item, i) => {
    const other = right[i];
    return (
      item.id === other.id &&
      item.trigger === other.trigger &&
      item.at === other.at &&
      (item.note || '') === (other.note || '') &&
      Boolean(item.forStart) === Boolean(other.forStart) &&
      Boolean(item.hold) === Boolean(other.hold) &&
      (item.askedAt || '') === (other.askedAt || '') &&
      item.to?.type === other.to?.type &&
      item.to?.id === other.to?.id &&
      recurSignature(item.recur) === recurSignature(other.recur)
    );
  });
}

function recurSignature(recur) {
  if (!recur) return '';
  return [
    recur.every,
    recur.unit,
    recur.time || '',
    recur.weekday ?? '',
    recur.monthDay ?? '',
    recur.end,
    recur.times ?? '',
    recur.until || '',
    recur.fired ?? 0,
    recur.anchor || '',
    recur.timeZone || '',
  ].join('\0');
}

export function addReminder(list, partial) {
  const current = normalizeReminders(list);
  const reminder = normalizeReminder({
    ...partial,
    id: partial?.id || newReminderId(),
    to: partial?.to || REMINDER_TO_ME,
    createdAt: partial?.createdAt || new Date().toISOString(),
  });
  if (!reminder) return current;
  if (
    reminder.trigger === REMINDER_TRIGGER.WHEN_AVAILABLE &&
    current.some((r) => r.trigger === REMINDER_TRIGGER.WHEN_AVAILABLE)
  ) {
    return current;
  }
  return [...current, reminder];
}

export function removeReminder(list, reminderId) {
  return normalizeReminders(list).filter((r) => r.id !== reminderId);
}

export function updateReminder(list, reminderId, patch) {
  return normalizeReminders(list).map((reminder) => {
    if (reminder.id !== reminderId) return reminder;
    const nextAt = Object.prototype.hasOwnProperty.call(patch, 'at') ? normalizeAt(patch.at) : reminder.at;
    const atChanged = nextAt !== reminder.at;
    return normalizeReminder({
      ...reminder,
      ...patch,
      id: reminder.id,
      createdAt: reminder.createdAt,
      hold: patch.hold !== undefined ? patch.hold : reminder.hold,
      askedAt: patch.askedAt !== undefined ? patch.askedAt : (atChanged ? undefined : reminder.askedAt),
    }) || reminder;
  });
}

export function removeReminders(list, reminderIds) {
  const drop = new Set(reminderIds || []);
  return normalizeReminders(list).filter((r) => !drop.has(r.id));
}

/**
 * After a fire: drop the reminder (`next` null) or replace it with the next occurrence.
 * @param {Array<{ id: string, next: object|null }>} patches
 */
export function applyReminderPatches(list, patches) {
  const byId = new Map((patches || []).map((patch) => [patch.id, patch.next]));
  const out = [];
  for (const reminder of normalizeReminders(list)) {
    if (!byId.has(reminder.id)) {
      out.push(reminder);
      continue;
    }
    const next = byId.get(reminder.id);
    if (!next) continue;
    const normalized = normalizeReminder({
      ...next,
      id: reminder.id,
      createdAt: reminder.createdAt,
    });
    if (normalized) out.push(normalized);
  }
  return out;
}

export function hasWhenAvailable(list) {
  return normalizeReminders(list).some((r) => r.trigger === REMINDER_TRIGGER.WHEN_AVAILABLE);
}

/** Wait-for rings stay on the card. askedAt is the occurrence that already asked. */
export function isHoldReminderAsked(reminder) {
  if (!reminder?.hold || !reminder.askedAt || !reminder.at) return false;
  const asked = Date.parse(reminder.askedAt);
  const at = Date.parse(reminder.at);
  return Number.isFinite(asked) && Number.isFinite(at) && asked >= at;
}

export function tasksHaveTimedReminders(tasks) {
  if (!Array.isArray(tasks)) return false;
  return tasks.some((task) =>
    normalizeReminders(task?.reminders).some((r) => r.trigger === REMINDER_TRIGGER.AT_TIME)
  );
}

/**
 * Timed reminders whose clock has arrived.
 * @returns {Array<{ task: object, reminder: object }>}
 */
export function collectDueReminders(tasks, nowMs) {
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const due = [];
  for (const task of tasks || []) {
    for (const reminder of normalizeReminders(task?.reminders)) {
      if (reminder.trigger !== REMINDER_TRIGGER.AT_TIME || !reminder.at) continue;
      if (isHoldReminderAsked(reminder)) continue;
      const at = Date.parse(reminder.at);
      if (Number.isFinite(at) && at <= now) {
        due.push({ task, reminder });
      }
    }
  }
  return due;
}

/**
 * Missions that left a locked state and still have a "when available" reminder.
 * First snapshot (prevLockedIds === null) never fires.
 */
export function collectAvailableReminderUnlocks(prevLockedIds, lockStates, tasks) {
  const nextLockedIds = new Set();
  for (const task of tasks || []) {
    if (lockStates?.[task.id]?.isLocked) nextLockedIds.add(task.id);
  }

  if (prevLockedIds == null) {
    return { nextLockedIds, unlocked: [] };
  }

  const unlocked = [];
  for (const id of prevLockedIds) {
    if (nextLockedIds.has(id)) continue;
    const task = (tasks || []).find((t) => t.id === id);
    if (!task || lockStates?.[id]?.isLocked) continue;
    const reminders = normalizeReminders(task.reminders).filter(
      (r) => r.trigger === REMINDER_TRIGGER.WHEN_AVAILABLE
    );
    if (reminders.length) unlocked.push({ task, reminders });
  }
  return { nextLockedIds, unlocked };
}

/**
 * "When available" added on a mission that is already unlocked.
 * First snapshot never fires.
 */
export function collectNewWhenAvailableOnUnlocked(prevIdsByTask, tasks, lockStates) {
  const nextIdsByTask = {};
  const fires = [];
  for (const task of tasks || []) {
    const ids = normalizeReminders(task?.reminders)
      .filter((r) => r.trigger === REMINDER_TRIGGER.WHEN_AVAILABLE)
      .map((r) => r.id);
    nextIdsByTask[task.id] = ids;
    if (prevIdsByTask == null) continue;
    if (lockStates?.[task.id]?.isLocked) continue;
    const prev = prevIdsByTask[task.id] || [];
    const added = ids.filter((id) => !prev.includes(id));
    if (added.length) {
      const reminders = normalizeReminders(task.reminders).filter((r) => added.includes(r.id));
      fires.push({ task, reminders });
    }
  }
  return { nextIdsByTask, fires };
}
